import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  LenderMappingsFormValues,
  LenderFieldMapWithErrors,
} from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { CurrencyInputField, InputField, NumberFormatField } from 'components/form';
import { sanitizeFormKey } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';
import { formatPhoneInput } from 'formatters';
import { isValidSSN, PHONE_REGEX, validationMessages } from 'shared';

interface Props {
  field: LenderFieldMapWithErrors;
  inputProps: any;
  formatter?: (value: string) => string;
  onValueChange: (lenderProperty: string, newValue: any) => void;
}

export const TextMappingField = ({ field, inputProps, formatter, onValueChange }: Props) => {
  const { control, getFieldState, formState } = useFormContext<LenderMappingsFormValues>();

  const type = field.details?.fieldInputType;
  const isRequired = field.details?.required === 'REQUIRED';
  const fieldName = `mappingsByLenderProperty.${sanitizeFormKey(field.lenderProperty!)}.mappedValue` as const;
  const rules = {
    validate: (value: unknown) => {
      if (isRequired && (value === null || value === undefined || value === '')) return 'This field is required';
      if (type === 'SSN' && value) {
        return isValidSSN(value as string) ? true : 'SSN must be valid';
      }
      if (type === 'PHONE_NUMBER' && value) {
        // formatPhoneInput truncates past 10 digits, so the digit count is checked separately.
        const digits = String(value).replace(/\D/g, '');
        return digits.length === 10 && PHONE_REGEX.test(formatPhoneInput(digits)) ? true : validationMessages.phone;
      }
      const num = Number(value);
      if (isRequired || (value !== null && value !== undefined && value !== '')) {
        if (inputProps.min !== undefined && num < Number(inputProps.min)) return `Min value is ${inputProps.min}`;
        if (inputProps.max !== undefined && num > Number(inputProps.max)) return `Max value is ${inputProps.max}`;
      }
      return true;
    },
  };

  const hasError = !!field.details?.errors?.length;
  const errorMessages = field.details?.errors?.map(e => e.message).join(', ');
  const label = field.details?.displayName ?? '';
  const rhfFieldError = getFieldState(fieldName, formState).error;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (field.lenderProperty) onValueChange(field.lenderProperty, e.target.value);
  };

  const range: [number | undefined, number | undefined] | undefined =
    inputProps.min !== undefined || inputProps.max !== undefined
      ? [
          inputProps.min !== undefined ? Number(inputProps.min) : undefined,
          inputProps.max !== undefined ? Number(inputProps.max) : undefined,
        ]
      : undefined;

  if (type === 'FLOAT' || type === 'INTEGER') {
    return (
      <NumberFormatField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        range={range}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          size: 'small',
          helperText: errorMessages,
          onBlur: handleBlur,
        }}
      />
    );
  }

  if (type === 'MONEY') {
    return (
      <CurrencyInputField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        range={range}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          helperText: errorMessages,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            if (field.lenderProperty) {
              onValueChange(field.lenderProperty, e.target.value.replace(/[^0-9.-]+/g, ''));
            }
          },
        }}
      />
    );
  }

  return (
    <InputField
      label={label}
      control={control}
      name={fieldName}
      rules={rules}
      formatter={formatter}
      inputProps={{
        error: hasError || !!rhfFieldError,
        required: isRequired,
        variant: 'outlined',
        helperText: errorMessages || rhfFieldError?.message,
        onBlur: handleBlur,
      }}
    />
  );
};
